// §9.2 / §11.1 — the file-type registry: which app opens a `.md`.
//
// Two inputs, and the order between them is the whole design. An app *declares* what it can open
// (`fileTypes` in its manifest, §9.2). The user *decides* what actually opens it (§11.6's Default
// apps). A declaration never beats a decision — installing an app must not silently take a file
// type away from the one already handling it.
import { extname, type VfsPath } from '@hangar/shared';
import type { InProcessFileType } from '@hangar/shared';

export type FileTypeRole = 'editor' | 'viewer';

export interface FileTypeMatch {
  appId: string;
  role: FileTypeRole;
  /** Why this app is in the list: the user chose it, or the app declared it. */
  reason: 'user-default' | 'extension' | 'mime';
}

export interface FileTypeRegistry {
  /** Called on install and for every built-in at boot. Re-registering an app replaces its set. */
  register(appId: string, types: readonly InProcessFileType[]): void;
  unregister(appId: string): void;
  /** §11.1 "Open with", best first. Empty for an extension nobody claims. */
  candidatesFor(path: VfsPath): FileTypeMatch[];
  /** The single app that opens `path`, or `null`. */
  defaultFor(path: VfsPath): string | null;
  /** Candidates for a MIME type rather than a path — §10's transfer targets use this. */
  candidatesForMime(mime: string): FileTypeMatch[];
  /** §11.6 — the user's explicit choice, keyed by lowercase extension including the dot. */
  setDefault(extension: string, appId: string): void;
  clearDefault(extension: string): void;
  /** Every extension any registered app claims, sorted. For the Default apps page. */
  knownExtensions(): string[];
  registrationsFor(appId: string): InProcessFileType[];
}

export interface FileTypeRegistryOptions {
  /** Reads `filetypes.defaults` from Settings; the registry never owns the persistence. */
  defaults: () => Record<string, string>;
  setDefaults: (next: Record<string, string>) => void;
}

/** Normalise `.MD`, `md`, `MD` to `.md`. */
export function normalizeExtension(input: string): string {
  const trimmed = input.trim().toLowerCase();
  if (trimmed.length === 0) return '';
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
}

/** A wildcard in either half: `text/<star>` matches `text/markdown`, `<star>/<star>` anything. */
export function mimeMatches(pattern: string, mime: string): boolean {
  const [patternType = '', patternSub = ''] = pattern.toLowerCase().split('/');
  const [type = '', sub = ''] = mime.toLowerCase().split('/');
  if (patternType !== '*' && patternType !== type) return false;
  return patternSub === '*' || patternSub === sub;
}

const ROLE_RANK: Record<FileTypeRole, number> = { editor: 0, viewer: 1 };

export function createFileTypeRegistry(options: FileTypeRegistryOptions): FileTypeRegistry {
  // Insertion order is registration order, which is the tie-break after role.
  const registrations = new Map<string, InProcessFileType[]>();

  function claims(appId: string): InProcessFileType[] {
    return registrations.get(appId) ?? [];
  }

  function byExtension(extension: string): FileTypeMatch[] {
    if (extension.length === 0) return [];
    const out: FileTypeMatch[] = [];
    for (const [appId, types] of registrations) {
      for (const type of types) {
        if (!type.ext.some((candidate) => normalizeExtension(candidate) === extension)) continue;
        out.push({ appId, role: type.role, reason: 'extension' });
        break; // one entry per app, whatever it declared
      }
    }
    return out;
  }

  /** Role first, then registration order — which `byExtension` already preserves. */
  function ranked(matches: FileTypeMatch[]): FileTypeMatch[] {
    return matches
      .map((match, index) => ({ match, index }))
      .sort((a, b) => {
        const byRole = ROLE_RANK[a.match.role] - ROLE_RANK[b.match.role];
        return byRole !== 0 ? byRole : a.index - b.index;
      })
      .map((entry) => entry.match);
  }

  return {
    register(appId, types) {
      registrations.set(
        appId,
        types.map((type) => ({
          ...type,
          ext: type.ext.map(normalizeExtension).filter((ext) => ext.length > 0),
        })),
      );
    },

    unregister(appId) {
      registrations.delete(appId);
      // A default pointing at an app that is gone is worse than no default: it makes the file
      // unopenable rather than falling through to whatever else can handle it.
      const defaults = options.defaults();
      const surviving = Object.fromEntries(
        Object.entries(defaults).filter(([, id]) => id !== appId),
      );
      if (Object.keys(surviving).length !== Object.keys(defaults).length) {
        options.setDefaults(surviving);
      }
    },

    candidatesFor(path) {
      const extension = extname(path);
      const declared = ranked(byExtension(extension));
      const chosen = options.defaults()[extension];
      if (chosen == null) return declared;

      // The user's choice goes first — even if the app that would otherwise win declared the
      // type as an editor and this one is a viewer. It is their decision, not the manifest's.
      const rest = declared.filter((match) => match.appId !== chosen);
      const role = declared.find((match) => match.appId === chosen)?.role ?? 'viewer';
      if (!registrations.has(chosen)) return declared; // the chosen app is not installed
      return [{ appId: chosen, role, reason: 'user-default' }, ...rest];
    },

    defaultFor(path) {
      return this.candidatesFor(path)[0]?.appId ?? null;
    },

    candidatesForMime(mime) {
      const out: FileTypeMatch[] = [];
      for (const [appId, types] of registrations) {
        for (const type of types) {
          if (!type.mime?.some((pattern) => mimeMatches(pattern, mime))) continue;
          out.push({ appId, role: type.role, reason: 'mime' });
          break;
        }
      }
      return ranked(out);
    },

    setDefault(extension, appId) {
      const key = normalizeExtension(extension);
      if (key.length === 0) return;
      options.setDefaults({ ...options.defaults(), [key]: appId });
    },

    clearDefault(extension) {
      const key = normalizeExtension(extension);
      const next = { ...options.defaults() };
      if (!(key in next)) return;
      delete next[key];
      options.setDefaults(next);
    },

    knownExtensions() {
      const all = new Set<string>();
      for (const types of registrations.values()) {
        for (const type of types) for (const ext of type.ext) all.add(ext);
      }
      return [...all].sort();
    },

    registrationsFor: (appId) => claims(appId).map((type) => ({ ...type })),
  };
}
